import { useEffect, useRef, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  ArrowLeft,
  Plus,
  X,
  Loader2,
  ChevronRight,
  ChevronDown,
  Copy,
  Pencil,
} from 'lucide-react'
import { Button } from '@/core/ui/Button'
import { Input } from '@/core/ui/Input'
import { useIsAuthenticated, useSession } from '@/core/auth/SessionStore'
import { PickerSheet } from '@/features/common/PickerSheet'
import { cn } from '@/lib/cn'
import {
  cloneViagem,
  errosDaViagem,
  errosDoPadrao,
  newViagemFromDefaults,
  totalProducao,
  totalValor,
  totalVolume,
  useCarreteiroStore,
  viagemProducao,
  type CarreteiroFichaState,
  type FichaDefaults,
  type ViagemDraft,
} from '@/features/carreteiro/carreteiroStore'
import type { ErroViagem } from '@/features/carreteiro/carreteiroRegras'
import { ViagemQuickSheet } from '@/features/carreteiro/ViagemQuickSheet'
import { ViagemFormSheet } from '@/features/carreteiro/ViagemFormSheet'
import { useServicosCarreteiroPorMve } from '@/features/carreteiro/useServicosCarreteiro'
import {
  useLocaisCargaPorProjeto,
  useLocaisDescargaRod,
  useLocaisDescargaUrb,
} from '@/core/db/daos'
import type {
  CatLocalCarga,
  CatLocalDescargaRod,
  CatLocalDescargaUrb,
  CatServico,
} from '@/core/db/entities'

/**
 * Step 1: "Padrão do dia" + Viagens.
 *
 * Filosofia (análise de 3853 fichas históricas): o Padrão do dia captura os
 * campos que não mudam viagem a viagem (serviço, origem, destino, DT fixo,
 * estaca origem, NF). As viagens entram por um modal rápido (hora, estaca,
 * volume, NF); exceções vão pelo formulário completo.
 *
 * Validação: regras do SIGA (carreteiroRegras). "+ Adicionar viagem" só
 * libera com o Padrão do dia válido; cada viagem com erro fica marcada.
 */
export function CarreteiroStep1Route() {
  const authed = useIsAuthenticated()
  const session = useSession()
  const navigate = useNavigate()
  const store = useCarreteiroStore()
  const userChapa = session?.userChapa ?? null

  const [quickDraft, setQuickDraft] = useState<ViagemDraft | null>(null)
  const [editingViagem, setEditingViagem] = useState<ViagemDraft | null>(null)

  useBackfillSnapshots()

  // Recusa do servidor apontando uma viagem: abre direto nela (uma vez).
  const abriuRejeicao = useRef(false)
  useEffect(() => {
    if (abriuRejeicao.current) return
    const alvo = store.rejeicao?.viagemId
    if (!alvo) return
    const v = store.viagens.find((x) => x.id === alvo)
    if (v) {
      abriuRejeicao.current = true
      setEditingViagem(v)
    }
  }, [store.rejeicao, store.viagens])

  if (!authed) return <Navigate to="/login" replace />
  if (store.obraId == null || store.mveId == null) {
    return <Navigate to="/carreteiro/step0" replace />
  }

  const errosPadrao = errosDoPadrao(store)
  const defaultsOk = errosPadrao.length === 0
  const hasViagens = store.viagens.length > 0
  const lastViagem = hasViagens ? store.viagens[store.viagens.length - 1]! : null

  function openQuickAdd() {
    if (!defaultsOk) return
    try {
      setQuickDraft(newViagemFromDefaults(store, userChapa, lastViagem))
    } catch (e) {
      console.warn(e)
    }
  }

  function repeatLast() {
    if (!lastViagem) return
    store.addOrUpdateViagem(cloneViagem(lastViagem, userChapa, store.fichaConcreto))
  }

  const destaque =
    store.rejeicao && editingViagem && store.rejeicao.viagemId === editingViagem.id
      ? { campo: store.rejeicao.campo, mensagem: store.rejeicao.message }
      : null

  return (
    <main className="min-h-svh bg-bg-base flex flex-col">
      <header className="sticky top-0 z-10 bg-bg-base/95 backdrop-blur border-b border-border pt-safe pl-safe pr-safe">
        <div className="flex items-center gap-2 px-2 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/carreteiro/step0')}
            aria-label="Voltar"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-on-surface">Carreteiro · 2 de 3 · Viagens</h1>
            <p className="text-xs text-on-surface-dim truncate">
              {store.mvePrefixo}
              {store.mvePlaca ? ` · ${store.mvePlaca}` : ''} ·{' '}
              {store.tipo === 'R' ? 'Rodoviário' : 'Urbana'}
            </p>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto pb-32">
        <div className="p-4 max-w-2xl mx-auto space-y-4">
          {store.rejeicao && (
            <div className="rounded-lg border border-bad bg-bad/10 px-4 py-3 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-bad shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-on-surface">Recusada pelo servidor</p>
                <p className="text-sm text-on-surface-dim mt-0.5">{store.rejeicao.message}</p>
                <p className="text-xs text-on-surface-dim mt-1">Corrija e salve a ficha de novo.</p>
              </div>
              <button
                type="button"
                onClick={store.limparRejeicao}
                className="text-on-surface-dim hover:text-on-surface"
                aria-label="Dispensar aviso"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <DefaultsPanel forceOpen={!hasViagens} erros={errosPadrao} />

          {/* Quilometragem (apenas rodoviário) — o SIGA web não tem; fica opcional. */}
          {store.tipo === 'R' && !store.mveProprio && <KilometragemCard />}

          {hasViagens && <StatsRow store={store} />}

          {hasViagens ? (
            <ul className="space-y-2">
              {store.viagens.map((v, idx) => (
                <li key={v.id}>
                  <ViagemCard
                    index={idx + 1}
                    viagem={v}
                    ficha={store}
                    onClick={() => setEditingViagem(v)}
                    onDelete={() => store.removeViagem(v.id)}
                  />
                </li>
              ))}
            </ul>
          ) : (
            defaultsOk && (
              <div className="card-surface-variant px-4 py-6 text-center">
                <p className="text-sm text-on-surface-dim">
                  Tudo pronto. Toque <span className="font-bold text-accent">+ Adicionar viagem</span>{' '}
                  ao final pra registrar a primeira.
                </p>
              </div>
            )
          )}
        </div>
      </div>

      {/* FABs (Repetir + Adicionar) — empilhados no canto */}
      <div className="fixed bottom-24 right-4 z-20 flex flex-col gap-3 items-end">
        {hasViagens && (
          <button
            type="button"
            onClick={repeatLast}
            disabled={!defaultsOk}
            className="inline-flex items-center gap-2 rounded-full bg-surface-2 text-on-surface px-4 py-2.5 font-semibold shadow-lg hover:bg-surface-2/80 active:opacity-80 transition-opacity disabled:opacity-50"
          >
            <Copy className="h-4 w-4" />
            Repetir última
          </button>
        )}
        <button
          type="button"
          onClick={openQuickAdd}
          disabled={!defaultsOk}
          className={cn(
            'inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold shadow-lg transition-opacity',
            defaultsOk
              ? 'bg-accent text-bg-base hover:opacity-90 active:opacity-80'
              : 'bg-surface-2 text-on-surface-dim cursor-not-allowed',
          )}
        >
          <Plus className="h-5 w-5" />
          Adicionar viagem
        </button>
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-bg-base/95 backdrop-blur border-t border-border p-4 pb-safe-plus-4 pl-safe pr-safe">
        <Button
          variant="primary"
          size="lg"
          disabled={!hasViagens}
          onClick={() => navigate('/carreteiro/step2')}
          className="w-full max-w-2xl mx-auto block"
        >
          Revisar e salvar
        </Button>
      </div>

      {quickDraft && (
        <ViagemQuickSheet
          ficha={store}
          draft={quickDraft}
          onSave={(v) => {
            store.addOrUpdateViagem(v)
            setQuickDraft(null)
          }}
          onAdvanced={(v) => {
            // Pula da quick pra full sheet com os valores em progresso
            setQuickDraft(null)
            setEditingViagem(v)
          }}
          onDismiss={() => setQuickDraft(null)}
        />
      )}

      {editingViagem && (
        <ViagemFormSheet
          ficha={store}
          initial={editingViagem}
          destaque={destaque}
          onSave={(v) => {
            store.addOrUpdateViagem(v)
            setEditingViagem(null)
          }}
          onDismiss={() => setEditingViagem(null)}
        />
      )}
    </main>
  )
}

/**
 * Rascunhos anteriores ao alinhamento não têm o tipo da origem nem o depósito
 * do destino (as regras de estaca/posição dependem deles). Preenche a partir
 * do catálogo local assim que ele carrega.
 */
function useBackfillSnapshots() {
  const store = useCarreteiroStore()
  const locaisCarga = useLocaisCargaPorProjeto(store.projetoId)
  const rod = useLocaisDescargaRod(store.projetoId)
  const urb = useLocaisDescargaUrb(store.projetoId)

  useEffect(() => {
    if (!locaisCarga || !rod || !urb) return
    const origem = new Map(locaisCarga.map((l) => [l.id, l.origem]))
    const deposito = new Map<number, string | null>(
      (store.tipo === 'R' ? rod : urb).map((l) => [l.id, l.deposito]),
    )
    const d = store.defaults
    const patch: Partial<FichaDefaults> = {}
    if (d.localCargaId != null && d.localCargaOrigem == null && origem.get(d.localCargaId)) {
      patch.localCargaOrigem = origem.get(d.localCargaId) ?? null
    }
    if (d.localDescargaId != null && d.localDescargaDeposito == null && deposito.get(d.localDescargaId)) {
      patch.localDescargaDeposito = deposito.get(d.localDescargaId) ?? null
    }
    let mudouViagem = false
    const viagens = store.viagens.map((v) => {
      const o = v.localCargaId != null && v.localCargaOrigem == null ? origem.get(v.localCargaId) : null
      const dep =
        v.localDescargaId != null && v.localDescargaDeposito == null ? deposito.get(v.localDescargaId) : null
      if (!o && !dep) return v
      mudouViagem = true
      return {
        ...v,
        localCargaOrigem: o ?? v.localCargaOrigem,
        localDescargaDeposito: dep ?? v.localDescargaDeposito,
      }
    })
    if (Object.keys(patch).length > 0 || mudouViagem) {
      useCarreteiroStore.setState({ defaults: { ...d, ...patch }, viagens })
    }
  }, [locaisCarga, rod, urb, store.tipo, store.defaults, store.viagens])
}

// ============================================================================
//  Painel "Padrão do dia" — collapsible
// ============================================================================

function DefaultsPanel({ forceOpen, erros }: { forceOpen: boolean; erros: ErroViagem[] }) {
  const store = useCarreteiroStore()
  const [open, setOpen] = useState(forceOpen)
  const [picker, setPicker] = useState<'servico' | 'origem' | 'destino' | null>(null)

  const servicosState = useServicosCarreteiroPorMve(store.mveId, store.dt)
  const locaisCarga = useLocaisCargaPorProjeto(store.projetoId) ?? []
  const locaisDescargaRod = useLocaisDescargaRod(store.projetoId) ?? []
  const locaisDescargaUrb = useLocaisDescargaUrb(store.projetoId) ?? []

  const d = store.defaults
  const complete = erros.length === 0
  const comNf = store.fichaConcreto === 'S'
  const servicoKm = d.servico?.nome.toLowerCase().includes('km') ?? false
  const dtFixoObrigatorio = store.tipo === 'U' || servicoKm
  const estacaOrigemObrigatoria =
    store.tipo === 'R' && (d.localCargaOrigem === 'P' || d.localDescargaDeposito === 'P')

  function alternarNf(checked: boolean) {
    if (
      store.viagens.length > 0 &&
      !confirm(`Todas as viagens passarão a ser ${checked ? 'com NF' : 'sem NF'}. Continuar?`)
    ) {
      return
    }
    store.setFichaConcreto(checked ? 'S' : 'N')
  }

  return (
    <div className="card-surface overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-surface-2/40 transition-colors"
      >
        <div className="flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-accent uppercase">Padrão do dia</span>
            {complete ? (
              <span className="text-[10px] uppercase tracking-wide bg-ok/15 text-ok px-1.5 py-0.5 rounded">
                Configurado
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wide bg-warn/15 text-warn px-1.5 py-0.5 rounded">
                Preencher
              </span>
            )}
          </div>
          {complete && !open && (
            <p className="text-xs text-on-surface-dim mt-1 truncate">
              {d.servico?.nome} · {d.localCargaNome} → {d.localDescargaNome}
              {comNf ? ' · com NF' : ''}
            </p>
          )}
        </div>
        {open ? (
          <ChevronDown className="h-5 w-5 text-on-surface-dim" />
        ) : (
          <ChevronRight className="h-5 w-5 text-on-surface-dim" />
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-4">
          <PickerField
            label="Serviço"
            value={d.servico?.nome ?? 'Selecionar…'}
            sublabel={
              d.servico?.materialNome
                ? `${d.servico.materialNome}${d.servico.unidadeNome ? ` (${d.servico.unidadeNome})` : ''}`
                : null
            }
            onClick={() => setPicker('servico')}
          />
          {(d.servico?.contratoItensVigentes ?? 0) > 1 && (
            <p className="text-xs text-warn">
              Existem mais de um item de contrato ativos para o serviço {d.servico?.nome}. O mais
              recente será usado.
            </p>
          )}

          {servicosState.loading && (
            <p className="text-xs text-on-surface-dim inline-flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Carregando serviços com contrato…
            </p>
          )}

          <PickerField
            label="Origem (local de carga)"
            value={d.localCargaNome ?? 'Selecionar…'}
            onClick={() => setPicker('origem')}
          />

          <PickerField
            label="Destino padrão"
            value={d.localDescargaNome ?? 'Selecionar…'}
            onClick={() => setPicker('destino')}
          />

          <div className={store.tipo === 'R' ? 'grid grid-cols-2 gap-3' : ''}>
            <NumericField
              label={dtFixoObrigatorio ? 'DT fixo (km)' : 'DT fixo (km) · opcional'}
              value={d.cargaDtFixo?.toString() ?? ''}
              onChange={(s) => store.setDefaults({ cargaDtFixo: parseNum(s) })}
            />
            {store.tipo === 'R' && (
              <LabeledInput
                label={estacaOrigemObrigatoria ? 'Estaca origem' : 'Estaca origem · opcional'}
                value={d.cargaEstacaOrigem ?? ''}
                onChange={(s) => store.setDefaults({ cargaEstacaOrigem: s.trim() || null })}
                placeholder="ex.: 00001"
                maxLength={6}
                inputMode="numeric"
              />
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none rounded-lg bg-surface-2/40 px-3 py-2.5">
            <input
              type="checkbox"
              checked={comNf}
              onChange={(e) => alternarNf(e.target.checked)}
              className="h-4 w-4 accent-accent"
            />
            <div className="flex-1 min-w-0">
              <span className="text-sm text-on-surface">Ficha com NF / ticket</span>
              <p className="text-xs text-on-surface-dim">
                Vale para TODAS as viagens da ficha (CBUQ, asfalto, concreto…). NF e quantidade são
                digitadas em cada viagem.
              </p>
            </div>
          </label>

          {/* Quantidade padrão só sem NF — com NF o volume vem do tíquete. */}
          {!comNf && (
            <NumericField
              label="Quantidade padrão (M³/Ton)"
              value={d.volume?.toString() ?? ''}
              onChange={(s) => store.setDefaults({ volume: parseNum(s) })}
            />
          )}

          {!complete && (
            <p className="text-xs text-warn">
              Preencha: {Array.from(new Set(erros.map((e) => e.mensagem))).join(' · ')}
            </p>
          )}
        </div>
      )}

      {picker === 'servico' && (
        <PickerSheet<CatServico>
          open
          title="Serviço (carreteiro)"
          items={servicosState.servicos}
          label={(s) => s.nome}
          sublabel={(s) => {
            const parts = [s.unidadeNome ?? '—']
            if (s.materialNome) parts.unshift(s.materialNome)
            if ((s.contratoItensVigentes ?? 0) > 1) parts.push('mais de um item de contrato')
            return parts.join(' · ')
          }}
          onPick={(s) => {
            store.setDefaults({ servico: s })
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar serviço"
          emptyMessage={
            servicosState.loading
              ? 'Carregando serviços…'
              : servicosState.source == null
                ? 'Sem rede e sem cache de serviços deste caminhão. Conecte e tente novamente.'
                : 'Nenhum serviço com contrato de carreteiro vigente para este caminhão.'
          }
          header={
            servicosState.loading ? (
              <div className="px-4 py-2 text-xs text-on-surface-dim flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Buscando serviços…
              </div>
            ) : servicosState.source === 'cache' ? (
              <div className="px-4 py-2 text-xs text-warn">
                Sem rede — serviços em cache. Contrato será validado no envio.
              </div>
            ) : null
          }
        />
      )}
      {picker === 'origem' && (
        <PickerSheet<CatLocalCarga>
          open
          title="Origem"
          items={locaisCarga}
          label={(l) => l.nome ?? l.complemento ?? '(sem identificação)'}
          onPick={(l) => {
            store.setDefaults({
              localCargaId: l.id,
              localCargaNome: l.nome ?? l.complemento ?? '(sem identificação)',
              localCargaOrigem: l.origem,
            })
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar origem"
          emptyMessage="Nenhuma origem cadastrada"
        />
      )}
      {picker === 'destino' && store.tipo === 'U' && (
        <PickerSheet<CatLocalDescargaUrb>
          open
          title="Destino"
          items={locaisDescargaUrb}
          label={(l) => l.nome}
          sublabel={(l) => l.bairro ?? null}
          onPick={(l) => {
            store.setDefaults({
              localDescargaId: l.id,
              localDescargaNome: l.nome,
              localDescargaDeposito: l.deposito,
            })
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar destino"
          emptyMessage="Nenhum destino cadastrado"
        />
      )}
      {picker === 'destino' && store.tipo === 'R' && (
        <PickerSheet<CatLocalDescargaRod>
          open
          title="Destino"
          items={locaisDescargaRod}
          label={(l) => l.nome}
          sublabel={(l) => l.deposito ?? null}
          onPick={(l) => {
            store.setDefaults({
              localDescargaId: l.id,
              localDescargaNome: l.nome,
              localDescargaDeposito: l.deposito,
            })
            setPicker(null)
          }}
          onDismiss={() => setPicker(null)}
          searchHint="Buscar destino"
          emptyMessage="Nenhum destino cadastrado"
        />
      )}
    </div>
  )
}

// ============================================================================
//  Quilometragem (rodoviário)
// ============================================================================

function KilometragemCard() {
  const store = useCarreteiroStore()
  return (
    <div className="card-surface-variant px-4 py-3">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-bold text-accent uppercase">Quilometragem</span>
        <span className="text-[10px] text-on-surface-dim">opcional</span>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-2">
        <NumericField
          label="KM inicial"
          value={store.kmInicial?.toString() ?? ''}
          onChange={(s) => store.setKmInicial(parseInt0(s))}
          integer
        />
        <NumericField
          label="KM final"
          value={store.kmFinal?.toString() ?? ''}
          onChange={(s) => store.setKmFinal(parseInt0(s))}
          integer
        />
      </div>
    </div>
  )
}

// ============================================================================
//  Stats row + ViagemCard
// ============================================================================

function StatsRow({ store }: { store: CarreteiroFichaState }) {
  const unidade = store.defaults.servico?.unidadeNome ?? ''
  const valor = totalValor(store)
  const fmt = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
  return (
    <div className="card-surface-variant px-4 py-3 grid grid-cols-2 gap-3 text-center">
      <Stat label="Viagens" value={String(store.viagens.length)} />
      <Stat label="Quantidade" value={fmt(totalVolume(store))} />
      <Stat label={`Produção${unidade ? ` (${unidade})` : ''}`} value={fmt(totalProducao(store))} />
      <Stat
        label="Valor estimado"
        value={
          valor == null
            ? '—'
            : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        }
      />
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-on-surface-dim uppercase">{label}</p>
      <p className="text-xl font-black text-accent mt-0.5">{value}</p>
    </div>
  )
}

function ViagemCard({
  index,
  viagem,
  ficha,
  onClick,
  onDelete,
}: {
  index: number
  viagem: ViagemDraft
  ficha: CarreteiroFichaState
  onClick: () => void
  onDelete: () => void
}) {
  const prod = viagemProducao(ficha, viagem)
  const erros = errosDaViagem(ficha, viagem)
  const recusada = ficha.rejeicao?.viagemId === viagem.id
  const delta =
    ficha.tipo === 'R' ? viagem.descargaEstacaDescarga ?? '—' : viagem.localDescargaNome ?? '—'

  return (
    <div
      className={cn(
        'card-surface flex items-stretch overflow-hidden',
        (erros.length > 0 || recusada) && 'border border-bad/70',
      )}
    >
      <button type="button" onClick={onClick} className="flex-1 p-3 text-left min-w-0">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              'inline-flex items-center justify-center h-7 w-7 rounded text-sm font-bold flex-shrink-0',
              erros.length > 0 ? 'bg-bad text-bg-base' : 'bg-accent text-bg-base',
            )}
          >
            {index}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-on-surface font-medium truncate">
              {viagem.cargaHora ?? '—'} → {viagem.descargaHora ?? '—'}
            </p>
            <p className="text-xs text-on-surface-dim truncate mt-0.5">
              {ficha.tipo === 'R' ? `Estaca: ${delta}` : delta}
              {viagem.nf ? ` · NF ${viagem.nf}` : ''}
              {viagem.volume != null ? ` · ${viagem.volume} m³` : ''}
            </p>
            {erros[0] && <p className="text-xs text-bad truncate mt-0.5">{erros[0].mensagem}</p>}
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-on-surface-dim">Prod</p>
            <p className="text-on-surface font-bold text-sm">
              {prod.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}
            </p>
          </div>
          <Pencil className="h-4 w-4 text-on-surface-dim opacity-50" />
        </div>
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="px-3 text-bad hover:bg-bad/10 transition-colors flex items-center"
        aria-label="Excluir"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

// ============================================================================
//  Primitivos locais
// ============================================================================

function PickerField({
  label,
  value,
  sublabel,
  onClick,
}: {
  label: string
  value: string
  sublabel?: string | null
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full card-surface-variant text-left px-4 py-3 flex items-center gap-3 min-h-touch hover:bg-surface-2/80 transition-colors"
    >
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-on-surface-dim uppercase">{label}</p>
        <p className="text-on-surface mt-0.5 truncate">{value}</p>
        {sublabel && <p className="text-xs text-on-surface-dim mt-0.5 truncate">{sublabel}</p>}
      </div>
      <ChevronRight className="h-5 w-5 text-on-surface-dim" />
    </button>
  )
}

function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  inputMode,
}: {
  label: string
  value: string
  onChange: (s: string) => void
  placeholder?: string
  maxLength?: number
  inputMode?: 'numeric' | 'text'
}) {
  return (
    <div>
      <label className="text-xs font-medium text-on-surface-dim uppercase">{label}</label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        inputMode={inputMode}
        className="mt-1"
      />
    </div>
  )
}

function NumericField({
  label,
  value,
  onChange,
  integer,
}: {
  label: string
  value: string
  onChange: (s: string) => void
  integer?: boolean
}) {
  const pattern = integer ? /^[0-9]*$/ : /^[0-9]*[.,]?[0-9]*$/
  return (
    <div>
      <label className="text-xs font-medium text-on-surface-dim uppercase">{label}</label>
      <Input
        type="text"
        inputMode={integer ? 'numeric' : 'decimal'}
        value={value}
        onChange={(e) => {
          const v = e.target.value
          if (v === '' || pattern.test(v)) onChange(integer ? v : v.replace(',', '.'))
        }}
        className="mt-1"
      />
    </div>
  )
}

function parseNum(s: string): number | null {
  if (s.trim() === '') return null
  const n = Number(s.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

function parseInt0(s: string): number | null {
  if (s.trim() === '') return null
  const n = parseInt(s, 10)
  return Number.isFinite(n) ? n : null
}
